(() => {
  const applyFilteredSystemContext = (root = document) => {
    const searchParams = new URLSearchParams(window.location.search);
    const platform = searchParams.get('filter.p.m.custom.platform');
    if (!platform) return;
    const systemTitle = searchParams.get('rpg_system_title') || platform;

    const systemHandleAliases = {
      'playstation-portable': 'psp',
      'playstation-vita': 'ps-vita',
      'xbox-series-x-s': 'xbox-series-x'
    };
    const platformHandle = systemTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const colorHandle = systemHandleAliases[platformHandle] || platformHandle;

    root.querySelectorAll?.('[data-rpg-system-landing]').forEach((landing) => {
      landing.querySelectorAll('[data-rpg-row-product][data-rpg-platform]').forEach((product) => {
        const productPlatform = product.dataset.rpgPlatform?.trim().toLowerCase();
        if (productPlatform !== platform.trim().toLowerCase()) product.remove();
      });

      const heading = landing.querySelector('.rpg-system-landing__header h1');
      if (heading) heading.textContent = systemTitle;

      [...landing.classList]
        .filter((className) => className.startsWith('rpg-console-color--'))
        .forEach((className) => landing.classList.remove(className));
      landing.classList.add(`rpg-console-color--${colorHandle}`);

      landing.querySelectorAll('.rpg-system-row__all:not(.rpg-system-row__all--discord)').forEach((link) => {
        const linkUrl = new URL(link.href, window.location.origin);
        linkUrl.searchParams.set('filter.p.m.custom.platform', platform);
        link.href = `${linkUrl.pathname}${linkUrl.search}`;
      });
    });
  };

  applyFilteredSystemContext(document);

  if (window.RpgSystemCarousel) {
    window.RpgSystemCarousel.initialize(document);
    return;
  }

  const initializedRows = new WeakSet();
  const rowRefreshers = new WeakMap();
  const observedRows = new WeakSet();

  const initializeLandingNow = (landing) => {
    const rows = [];
    if (landing.matches?.('[data-rpg-system-row]')) rows.push(landing);
    landing.querySelectorAll?.('[data-rpg-system-row]').forEach((row) => rows.push(row));
    rows.forEach((row) => {
      if (initializedRows.has(row)) return;

      const track = row.querySelector('[data-rpg-row-products]');
      const previous = row.querySelector('[data-rpg-row-previous]');
      const next = row.querySelector('[data-rpg-row-next]');
      if (!track || !previous || !next) return;
      initializedRows.add(row);

      const revealRow = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => row.classList.add('is-ready'));
        });
      };

      const shuffle = (items) => {
        const output = [...items];
        for (let index = output.length - 1; index > 0; index -= 1) {
          const swapIndex = Math.floor(Math.random() * (index + 1));
          [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
        }
        return output;
      };

      const balanceProducts = (items) => {
        const buckets = new Map();
        shuffle(items).forEach((item) => {
          const card = item.querySelector('.rpg-game-card');
          const key = card?.dataset.rpgCombination || 'other';
          if (!buckets.has(key)) buckets.set(key, []);
          buckets.get(key).push(item);
        });

        const result = [];
        let lastKey = '';
        while (result.length < items.length) {
          const choices = shuffle(Array.from(buckets.entries()).filter(([, values]) => values.length));
          const nextChoice = choices.find(([key]) => key !== lastKey) || choices[0];
          if (!nextChoice) break;
          const [key, values] = nextChoice;
          result.push(values.pop());
          lastKey = key;
        }
        return result;
      };

      const candidates = [...track.querySelectorAll('[data-rpg-row-product]')];
      const preserveOrder = row.dataset.rpgRowOrder === 'preserve';
      const sortedCandidates = row.dataset.rpgRowSort === 'newest'
        ? candidates.sort((left, right) => Number(right.dataset.rpgNewInDate || 0) - Number(left.dataset.rpgNewInDate || 0))
        : candidates;
      const selectedCandidates = preserveOrder ? sortedCandidates.slice(0, 25) : shuffle(sortedCandidates).slice(0, 25);
      const selectedSet = new Set(selectedCandidates);
      candidates.forEach((candidate) => {
        if (!selectedSet.has(candidate)) candidate.remove();
      });
      const orderedProducts = preserveOrder ? selectedCandidates : balanceProducts(selectedCandidates);
      if (!orderedProducts.length) {
        previous.hidden = true;
        next.hidden = true;
        revealRow();
        return;
      }
      const uniqueProductCount = orderedProducts.length;
      const products = [...orderedProducts];
      const stableNaturalCount = uniqueProductCount === 3 || uniqueProductCount === 5 || uniqueProductCount === 7;
      const repeatedSequenceTarget = uniqueProductCount < 9 && !stableNaturalCount
        ? 9
        : Math.max(3, uniqueProductCount);
      let repeatedProductIndex = 0;
      while (products.length < repeatedSequenceTarget) {
        const repeatedProduct = orderedProducts[repeatedProductIndex % orderedProducts.length].cloneNode(true);
        repeatedProduct.dataset.rpgRepeatedProduct = 'true';
        products.push(repeatedProduct);
        repeatedProductIndex += 1;
      }
      if (products.length > uniqueProductCount && uniqueProductCount > 2) {
        const firstGroup = products[0].querySelector('.rpg-game-card')?.dataset.gameGroupId;
        const lastGroup = products.at(-1).querySelector('.rpg-game-card')?.dataset.gameGroupId;
        if (firstGroup && firstGroup === lastGroup) {
          const replacementProduct = orderedProducts[1].cloneNode(true);
          replacementProduct.dataset.rpgRepeatedProduct = 'true';
          products[products.length - 1] = replacementProduct;
        }
      }
      products.forEach((product) => track.append(product));

      const fitTabletTitles = () => {
        const tabletLayout = matchMedia('(min-width: 750px) and (max-width: 1099px)').matches;
        products.forEach((product) => {
          const title = product.querySelector('.rpg-game-card__title');
          const link = title?.querySelector('a');
          if (!title || !link) return;

          title.style.removeProperty('--rpg-card-title-fit');
          if (!tabletLayout) return;

          const minimum = 15.48;
          const maximum = 32;
          let lower = minimum;
          let upper = maximum;

          for (let pass = 0; pass < 8; pass += 1) {
            const candidate = (lower + upper) / 2;
            title.style.setProperty('--rpg-card-title-fit', `${candidate.toFixed(2)}px`);
            const titleBounds = title.getBoundingClientRect();
            const textRange = document.createRange();
            textRange.selectNodeContents(link);
            const lineRects = [...textRange.getClientRects()].filter((rect) => rect.width > 0 && rect.height > 0);
            const lineTops = new Set(lineRects.map((rect) => Math.round(rect.top)));
            const fitsLines = lineTops.size <= 2;
            const fitsBounds = lineRects.every((rect) => (
              rect.left >= titleBounds.left - 1
              && rect.right <= titleBounds.right + 1
              && rect.top >= titleBounds.top - 1
              && rect.bottom <= titleBounds.bottom + 1
            ));
            if (fitsLines && fitsBounds) lower = candidate;
            else upper = candidate;
          }

          title.style.setProperty('--rpg-card-title-fit', `${lower.toFixed(2)}px`);
        });
      };

      fitTabletTitles();
      document.fonts?.ready.then(() => {
        fitTabletTitles();
      });

      track.querySelectorAll('img').forEach((image) => {
        const useFallback = () => {
          const fallbackSource = image.dataset.rpgFallbackSrc;
          if (!fallbackSource || image.dataset.rpgFallbackTried === 'true') return;
          image.dataset.rpgFallbackTried = 'true';
          image.src = fallbackSource;
        };
        image.addEventListener('error', useFallback);
        image.loading = 'lazy';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        if (image.complete && image.naturalWidth === 0) useFallback();
      });

      const prioritizeProductImages = (product, priority = 'auto') => {
        product.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          image.fetchPriority = priority;
        });
      };

      let active = 0;
      let touchStartX = null;
      let touchStartY = null;
      let touchAnchorX = null;
      let touchAxis = null;
      let touchDistance = 0;
      let touchLastX = null;
      let touchLastTime = 0;
      let touchVelocity = 0;
      let pointerStartX = null;
      let pointerAnchorX = null;
      let pointerLastX = null;
      let pointerLastTime = 0;
      let pointerVelocity = 0;
      let pointerDistance = 0;
      let pointerId = null;
      let momentumFrame = 0;
      let suppressClick = false;
      let cachedStepWidth = 0;
      const visibleCount = () => {
        const forcedVisibleCount = Number(row.dataset.rpgRowVisibleCount);
        if (Number.isFinite(forcedVisibleCount) && forcedVisibleCount > 0) {
          return Math.min(forcedVisibleCount, uniqueProductCount >= 3 ? uniqueProductCount : 3);
        }
        const rowWidth = row.getBoundingClientRect().width;
        const deviceCapacity = rowWidth >= 2000 ? 7 : window.innerWidth >= 750 ? 5 : 3;
        const inventoryCapacity = uniqueProductCount >= 7 ? 7 : uniqueProductCount >= 5 ? 5 : 3;
        return Math.min(deviceCapacity, inventoryCapacity);
      };
      const stepWidth = () => {
        if (cachedStepWidth > 0) return cachedStepWidth;
        const first = track.querySelector('[data-rpg-row-product]');
        const trackStyle = getComputedStyle(track);
        const productWidth = first ? parseFloat(getComputedStyle(first).width) || 0 : 0;
        const gap = parseFloat(trackStyle.columnGap) || 0;
        cachedStepWidth = productWidth + gap;
        return cachedStepWidth;
      };

      const loopOffset = (index, activeIndex) => {
        let offset = (index - activeIndex + products.length) % products.length;
        if (offset > products.length / 2) offset -= products.length;
        return offset;
      };

      const arrange = (nextActive = active, animate = true) => {
        active = (nextActive + products.length) % products.length;
        const currentVisibleCount = visibleCount();
        track.dataset.rpgRowVisible = String(currentVisibleCount);
        const half = Math.floor(currentVisibleCount / 2);
        const step = stepWidth();

        products.forEach((product, index) => {
          const previousOffset = Number(product.dataset.rpgWheelOffset);
          const offset = loopOffset(index, active);
          const wrapped = animate
            && Number.isFinite(previousOffset)
            && Math.abs(offset - previousOffset) > products.length / 2;
          const desiredSlot = offset + half;

          product.classList.toggle('is-active', offset === 0);
          product.classList.toggle('is-near', Math.abs(offset) === 1);
          product.classList.toggle('is-outside', Math.abs(offset) > half);
          product.classList.toggle('is-jump', !animate || wrapped);
          product.style.setProperty('--rpg-wheel-shift', `${(desiredSlot - index) * step}px`);
          product.dataset.rpgWheelOffset = String(offset);

          if (Math.abs(offset) <= half + 1) {
            prioritizeProductImages(product, offset === 0 ? 'high' : 'auto');
          }

          product.tabIndex = offset === 0 || Math.abs(offset) > half ? -1 : 0;
          if (offset === 0) {
            product.removeAttribute('aria-label');
          } else if (Math.abs(offset) <= half) {
            const title = product.querySelector('.rpg-game-card__title')?.textContent.trim() || 'game';
            product.setAttribute('aria-label', `Center ${title}`);
          }

          product.querySelectorAll('a, button').forEach((control) => {
            if (offset === 0) {
              if (control.dataset.rpgWheelTabindex === '') {
                control.removeAttribute('tabindex');
              } else if (control.dataset.rpgWheelTabindex !== undefined) {
                control.setAttribute('tabindex', control.dataset.rpgWheelTabindex);
              }
              delete control.dataset.rpgWheelTabindex;
            } else {
              if (control.dataset.rpgWheelTabindex === undefined) {
                control.dataset.rpgWheelTabindex = control.getAttribute('tabindex') || '';
              }
              control.setAttribute('tabindex', '-1');
            }
          });
        });

        requestAnimationFrame(() => {
          products.forEach((product) => product.classList.remove('is-jump'));
        });

        previous.hidden = products.length < 2;
        next.hidden = products.length < 2;
      };

      const move = (direction) => {
        if (products.length < 2) return;
        arrange(active + direction);
      };

      const clearDragPresentation = () => {
        track.style.removeProperty('--rpg-wheel-drag');
        products.forEach((product) => {
          product.style.removeProperty('--rpg-wheel-live-opacity');
          product.style.removeProperty('--rpg-wheel-live-scale');
        });
      };

      const presentDrag = (distance) => {
        const step = Math.max(1, stepWidth());
        const mobile = matchMedia('(max-width: 749px)').matches;
        const half = Math.floor(visibleCount() / 2);
        const centerScale = mobile ? 1.16 : 1.08;
        const nearScale = mobile ? 0.78 : 0.9;
        const outerScale = mobile ? 0.78 : 0.72;
        const nearOpacity = mobile ? 0.58 : 0.76;
        const outerOpacity = mobile ? 0.58 : 0.42;
        const dragSlots = distance / step;

        track.style.setProperty('--rpg-wheel-drag', `${distance}px`);
        products.forEach((product) => {
          const offset = Number(product.dataset.rpgWheelOffset) + dragSlots;
          const distanceFromCenter = Math.abs(offset);
          let scale;
          let opacity;

          if (distanceFromCenter <= 1) {
            scale = centerScale + ((nearScale - centerScale) * distanceFromCenter);
            opacity = 1 + ((nearOpacity - 1) * distanceFromCenter);
          } else if (distanceFromCenter <= 2) {
            const progress = distanceFromCenter - 1;
            scale = nearScale + ((outerScale - nearScale) * progress);
            const nextOpacity = half === 1 ? 0 : outerOpacity;
            opacity = nearOpacity + ((nextOpacity - nearOpacity) * progress);
          } else {
            scale = outerScale;
            opacity = distanceFromCenter <= half
              ? outerOpacity
              : Math.max(0, outerOpacity * (1 - Math.min(1, distanceFromCenter - half)));
          }

          product.style.setProperty('--rpg-wheel-live-scale', scale.toFixed(4));
          product.style.setProperty('--rpg-wheel-live-opacity', opacity.toFixed(4));
        });
      };

      const cancelMomentum = () => {
        if (!momentumFrame) return;
        cancelAnimationFrame(momentumFrame);
        momentumFrame = 0;
      };

      const consumeDistance = (distance) => {
        const step = Math.max(1, stepWidth());
        let residual = distance;
        while (Math.abs(residual) >= step / 2) {
          const direction = residual > 0 ? 1 : -1;
          arrange(active - direction, false);
          residual -= direction * step;
        }
        presentDrag(residual);
        return residual;
      };

      const settleMomentum = (distance) => {
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion || Math.abs(distance) < 0.5) {
          track.classList.remove('is-dragging');
          clearDragPresentation();
          arrange(active);
          return;
        }

        const startedAt = performance.now();
        const duration = 260;
        const settle = (now) => {
          const progress = Math.min(1, (now - startedAt) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          presentDrag(distance * (1 - eased));
          if (progress < 1) {
            momentumFrame = requestAnimationFrame(settle);
            return;
          }
          momentumFrame = 0;
          track.classList.remove('is-dragging');
          clearDragPresentation();
          arrange(active);
        };
        momentumFrame = requestAnimationFrame(settle);
      };

      const startMomentum = (distance, velocity) => {
        let residual = distance;
        let currentVelocity = Math.max(-2.4, Math.min(2.4, velocity));
        let previousTime = performance.now();

        if (Math.abs(currentVelocity) < 0.04) {
          settleMomentum(residual);
          return;
        }

        const coast = (now) => {
          const elapsed = Math.min(32, Math.max(1, now - previousTime));
          previousTime = now;
          residual = consumeDistance(residual + (currentVelocity * elapsed));
          currentVelocity *= Math.exp(-0.0048 * elapsed);

          if (Math.abs(currentVelocity) >= 0.018) {
            momentumFrame = requestAnimationFrame(coast);
            return;
          }
          momentumFrame = 0;
          settleMomentum(residual);
        };
        momentumFrame = requestAnimationFrame(coast);
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
          return;
        }
        const product = event.target.closest('[data-rpg-row-product]');
        if (!product || product.classList.contains('is-active')) return;
        event.preventDefault();
        event.stopPropagation();
        const offset = Number(product.dataset.rpgWheelOffset);
        if (Number.isFinite(offset)) arrange(active + offset);
      });
      track.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const product = event.target.closest('[data-rpg-row-product]');
        if (!product || product.classList.contains('is-active')) return;
        event.preventDefault();
        const offset = Number(product.dataset.rpgWheelOffset);
        if (Number.isFinite(offset)) arrange(active + offset);
      });
      track.addEventListener('touchstart', (event) => {
        cancelMomentum();
        clearDragPresentation();
        touchStartX = event.touches[0]?.clientX ?? null;
        touchStartY = event.touches[0]?.clientY ?? null;
        touchAnchorX = touchStartX;
        touchAxis = null;
        touchDistance = 0;
        touchLastX = touchStartX;
        touchLastTime = performance.now();
        touchVelocity = 0;
        track.classList.add('is-dragging');
      }, { passive: true });
      track.addEventListener('touchmove', (event) => {
        if (touchStartX === null || touchStartY === null || touchAnchorX === null) return;
        const touch = event.touches[0];
        if (!touch) return;
        const totalDistanceX = touch.clientX - touchStartX;
        const distanceY = touch.clientY - touchStartY;
        const absoluteX = Math.abs(totalDistanceX);
        const absoluteY = Math.abs(distanceY);

        if (touchAxis === null && Math.max(absoluteX, absoluteY) >= 6) {
          touchAxis = absoluteY > absoluteX * 2.75 ? 'vertical' : 'horizontal';
        }
        if (touchAxis === 'vertical') {
          clearDragPresentation();
          return;
        }
        if (touchAxis !== 'horizontal') return;

        event.preventDefault();

        touchDistance = totalDistanceX;
        const step = Math.max(1, stepWidth());
        const now = performance.now();
        const elapsed = Math.max(1, now - touchLastTime);
        const instantVelocity = (touch.clientX - touchLastX) / elapsed;
        touchVelocity = (touchVelocity * 0.58) + (instantVelocity * 0.42);
        touchLastX = touch.clientX;
        touchLastTime = now;

        let residualDistance = touch.clientX - touchAnchorX;
        while (Math.abs(residualDistance) >= step / 2) {
          const direction = residualDistance > 0 ? 1 : -1;
          arrange(active - direction, false);
          touchAnchorX += direction * step;
          residualDistance = touch.clientX - touchAnchorX;
        }
        presentDrag(residualDistance);
      }, { passive: false });
      track.addEventListener('touchend', (event) => {
        if (touchStartX === null || touchAnchorX === null) return;
        const endX = event.changedTouches[0]?.clientX ?? touchStartX;
        const endY = event.changedTouches[0]?.clientY ?? touchStartY;
        const distance = touchDistance || endX - touchStartX;
        const distanceY = endY - touchStartY;
        const residualDistance = endX - touchAnchorX;
        const gestureAxis = touchAxis || (
          Math.max(Math.abs(distance), Math.abs(distanceY)) >= 6
            ? (Math.abs(distanceY) > Math.abs(distance) * 2.75 ? 'vertical' : 'horizontal')
            : null
        );
        const releaseVelocity = touchVelocity;
        touchStartX = null;
        touchStartY = null;
        touchAnchorX = null;
        touchAxis = null;
        touchDistance = 0;
        touchLastX = null;
        touchLastTime = 0;
        touchVelocity = 0;
        if (gestureAxis !== 'horizontal') {
          track.classList.remove('is-dragging');
          clearDragPresentation();
          arrange(active);
          return;
        }
        if (Math.abs(distance) > 24) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 400);
        }
        startMomentum(residualDistance, releaseVelocity);
      }, { passive: true });
      track.addEventListener('touchcancel', () => {
        cancelMomentum();
        touchStartX = null;
        touchStartY = null;
        touchAnchorX = null;
        touchAxis = null;
        touchDistance = 0;
        touchLastX = null;
        touchLastTime = 0;
        touchVelocity = 0;
        track.classList.remove('is-dragging');
        clearDragPresentation();
        arrange(active);
      }, { passive: true });
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        cancelMomentum();
        clearDragPresentation();
        pointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerAnchorX = event.clientX;
        pointerLastX = event.clientX;
        pointerLastTime = performance.now();
        pointerVelocity = 0;
        pointerDistance = 0;
      });
      track.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || event.pointerId !== pointerId || pointerStartX === null || pointerAnchorX === null) return;
        pointerDistance = event.clientX - pointerStartX;
        if (Math.abs(pointerDistance) < 5) return;
        if (!track.hasPointerCapture(pointerId)) {
          track.setPointerCapture(pointerId);
          track.classList.add('is-dragging');
        }
        const step = Math.max(1, stepWidth());
        const now = performance.now();
        const elapsed = Math.max(1, now - pointerLastTime);
        const instantVelocity = (event.clientX - pointerLastX) / elapsed;
        pointerVelocity = (pointerVelocity * 0.58) + (instantVelocity * 0.42);
        pointerLastX = event.clientX;
        pointerLastTime = now;

        let residualDistance = event.clientX - pointerAnchorX;
        while (Math.abs(residualDistance) >= step / 2) {
          const direction = residualDistance > 0 ? 1 : -1;
          arrange(active - direction, false);
          pointerAnchorX += direction * step;
          residualDistance = event.clientX - pointerAnchorX;
        }
        presentDrag(residualDistance);
      });
      const finishPointerDrag = (event, cancelled = false) => {
        if (event.pointerType !== 'mouse' || event.pointerId !== pointerId || pointerStartX === null || pointerAnchorX === null) return;
        const residualDistance = event.clientX - pointerAnchorX;
        const releaseVelocity = pointerVelocity;
        const dragged = Math.abs(pointerDistance) > 8;
        if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
        pointerId = null;
        pointerStartX = null;
        pointerAnchorX = null;
        pointerLastX = null;
        pointerLastTime = 0;
        pointerVelocity = 0;
        pointerDistance = 0;

        if (!dragged) {
          track.classList.remove('is-dragging');
          clearDragPresentation();
          arrange(active);
          return;
        }
        if (dragged) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 400);
        }
        if (cancelled) {
          track.classList.remove('is-dragging');
          clearDragPresentation();
          arrange(active);
          return;
        }
        startMomentum(residualDistance, releaseVelocity);
      };
      track.addEventListener('pointerup', (event) => finishPointerDrag(event));
      track.addEventListener('pointercancel', (event) => finishPointerDrag(event, true));
      if ('ResizeObserver' in window) {
        let observedTrackWidth = 0;
        let resizeFrame = 0;
        const layoutObserver = new ResizeObserver((entries) => {
          const trackEntry = entries.find((entry) => entry.target === track);
          const nextWidth = trackEntry?.contentRect.width || track.getBoundingClientRect().width;
          if (Math.abs(nextWidth - observedTrackWidth) < 0.5) return;
          observedTrackWidth = nextWidth;
          if (resizeFrame) return;
          resizeFrame = requestAnimationFrame(() => {
            resizeFrame = 0;
            cachedStepWidth = 0;
            fitTabletTitles();
            arrange(active, false);
          });
        });
        layoutObserver.observe(track);
        layoutObserver.observe(products[0]);
      } else {
        let resizeFrame = 0;
        window.addEventListener('resize', () => {
          if (resizeFrame) return;
          resizeFrame = requestAnimationFrame(() => {
            resizeFrame = 0;
            cachedStepWidth = 0;
            fitTabletTitles();
            arrange(active, false);
          });
        }, { passive: true });
      }
      rowRefreshers.set(row, () => {
        cachedStepWidth = 0;
        fitTabletTitles();
        arrange(active, false);
      });
      arrange(0, false);
      revealRow();
    });
  };

  const initializeLanding = (landing) => {
    const rows = [];
    if (landing.matches?.('[data-rpg-system-row]')) rows.push(landing);
    landing.querySelectorAll?.('[data-rpg-system-row]').forEach((row) => rows.push(row));

    rows.forEach((row) => {
      if (observedRows.has(row)) return;
      observedRows.add(row);

      if (row.closest('[data-rpg-home]')) {
        const initializeHomeRow = () => initializeLandingNow(row);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(initializeHomeRow, { timeout: 650 });
        } else {
          window.setTimeout(initializeHomeRow, 160);
        }
        return;
      }

      if (!('IntersectionObserver' in window)) {
        initializeLandingNow(row);
        return;
      }

      const rowObserver = new IntersectionObserver(([entry], observer) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        initializeLandingNow(row);
      }, { rootMargin: '700px 0px' });
      rowObserver.observe(row);
    });
  };

  const initialize = (root = document) => {
    if (root.matches?.('[data-rpg-system-landing]')) initializeLanding(root);
    root.querySelectorAll?.('[data-rpg-system-landing]').forEach(initializeLanding);
  };

  const refresh = (root = document) => {
    const rows = [];
    if (root.matches?.('[data-rpg-system-row]')) rows.push(root);
    root.querySelectorAll?.('[data-rpg-system-row]').forEach((row) => rows.push(row));
    rows.forEach((row) => rowRefreshers.get(row)?.());
  };

  window.RpgSystemCarousel = { initialize, refresh };
  initialize(document);
})();
